import * as WalkEditorReact from "react";
import { createWalkNameEditor } from "../../../../backend/src/shared/WalkNameEditor";
import type { WalkNameSnapshot } from "../../../../backend/src/shared/walkName";
/**
 * Walk detail — the transcript with speakers, the photos, the items the walk
 * produced, and links into the full review queue.
 *
 * ## THERE IS NO AUDIO ON THIS PAGE, AND THAT IS THE POINT
 *
 * The source design draws, on this exact screen: a play button, a 60-bar
 * waveform, and a `02:41 / 36:00` playhead. Spec §7.3 and §3.3 cut all of it.
 * Raw walk audio exists so GCP's speech API can diarize it; the user-facing
 * evidence is the transcript, the speaker labels and the photos.
 *
 * This file therefore contains:
 *
 *  - no `<audio>`, `<video>` or `<source>` element;
 *  - no play control, no scrubber, no waveform, no bar array;
 *  - no playhead, no elapsed-of-total, no per-clip length.
 *
 * It DOES print the walk's own start, end and recorded length. That is visit
 * metadata — how long the superintendent was on site — and it is the same
 * figure People already ships (spec §5.2), derived through the same shared
 * helper. The rule bans exposing what the audio SAYS; it does not ban saying
 * how long the walk was. MEDIA duration — the length of one captured clip —
 * is a different fact about a different object and stays cut: `WalkPhoto` has
 * no field for it.
 *
 * The rule is not held here alone, which is the important part: `getWalkDetail`
 * names the visit fields it projects, so `audioUrl` and `audioStartMs` never
 * reach the browser at all, `recordingSpans` is summed and dropped rather than
 * emitted, and photos pass the projection's `PRESENTABLE_MEDIA_TYPES`
 * allowlist before they are presigned. A product rule enforced only by a view
 * not rendering a field it has already received is not enforced.
 *
 * The design's PATH card ("Spatial view · later") is not built either — the
 * design itself marks it as future work, and there is no path data on a
 * `Visit` to draw one from.
 *
 * ## Review decisions happen with the full record visible
 *
 * `review` on the response is `getReviewQueue(scope, { visitId })` — the same
 * groups and provenance. Pending rows link to the matching expanded card in
 * Review, where all fields and evidence are visible before a decision.
 * The walk's title-only list does not offer Accept, Edit or Reject.
 *
 * ## The transcript belongs to the walker
 *
 * An org member can open a co-member's walk — the header, the length, the
 * photos, the records it produced and the review queue are all shared. What
 * was SAID is not: `getWalkDetail` withholds the transcript and the speaker
 * roster from anyone but the walk's own `userId`, the same rule the phone
 * enforces in `JobSiteManager`.
 *
 * This view therefore never says "this walk has no transcript" on the
 * strength of an empty array — `transcriptOwnerOnly` distinguishes withheld
 * from absent, and so does the "captured nothing" panel at the foot of the
 * page, which is a claim about the walk that a co-member has not been shown
 * enough to be told.
 *
 * ## Speakers are `Speaker 1`, `Speaker 2`, …
 *
 * The design prints real names. Nothing in the data supports that: a
 * transcript segment carries a diarization cluster id, and there is no
 * speaker→person mapping anywhere in `backend/`. The console prints what
 * `backend`'s own walk UI prints, so one voice reads the same on both.
 */

import { PhotoImage } from "../components/PhotoImage";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PhotoAnnotationEditor } from "../components/PhotoAnnotationEditor";
import { PhotoFullscreenViewer, type FullscreenPhoto } from "../components/PhotoFullscreenViewer";
import { useAuthPerson } from "../auth/identity";
import {
  addPhotoComment,
  fetchPhotoComments,
  fetchPhotoTicket,
  fetchWalkDetail,
  postMutation,
  updateWalkPhotoDescription,
  type ApiError,
  type ConsoleItemKind,
  type ConsoleRow,
  type WalkDetailResponse,
  type PhotoCommentResponse,
  type WalkHeaderResponse,
  type WalkPhotoResponse,
  type WalkTranscriptLineResponse,
} from "../api";
import { useMediaQuery } from "../shell/useMediaQuery";
import {
  downloadFile,
  photoFileUrl,
  photosZipUrl,
  setDragFile,
} from "../lib/photoTransfer";
import { useResource } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { walkerLabel } from "./walkerLabel";
import { mobileRecordKindLabel } from "../mobileRecordPresentation";
import { C, FONT_MONO, projectDot } from "../tokens";
import { WALK_COLUMNS } from "../layout";
import {
  ASK_COMPOSER_CLEARANCE,
  Card,
  ConsoleViewProps,
  EmptyPanel,
  EvidenceTile,
  FailurePanel,
  KindChip,
  KIND_ICON,
  KIND_LABEL,
  LoadingPanel,
  META,
  QuietLine,
  ROW_LINE,
  ROW_TITLE,
  StaleBanner,
  clockOf,
  dayLabel,
  humanize,
  whenStamp,
} from "../components/chrome";
import { reviewRecordHref } from "../lib/reviewHref";

/**
 * This page is a readable account of a walk, not a hand-off surface. Copying
 * belongs on an individual record detail, where a person has selected the
 * exact field they want to reuse.
 */

/**
 * A ticket for this walk's photos, kept fresh.
 *
 * Fetched when the page opens, not when a drag starts: `dragstart` is
 * synchronous, so a ticket that has to be awaited cannot be used. Refreshed a
 * minute before it expires, because a walk page left open on a second monitor
 * for an hour should still be able to hand over a file. `null` while it is in
 * flight and after a failure — every caller degrades rather than blocking (see
 * `lib/photoTransfer.ts`).
 */
function usePhotoTicket(visitId: string, search: string, enabled: boolean): string | null {
  const [ticket, setTicket] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const load = () => {
      void fetchPhotoTicket(visitId, search)
        .then((response) => {
          if (cancelled) return;
          setTicket(response.ticket);
          const early = response.expiresAt - Date.now() - 60_000;
          timer = setTimeout(load, Math.max(30_000, early));
        })
        .catch(() => {
          // No ticket: downloads fall back to an authenticated fetch and the
          // drag falls back to the image URL. Nothing to say on screen — the
          // buttons still work.
          if (!cancelled) setTicket(null);
        });
    };
    load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [visitId, search, enabled]);

  return ticket;
}

/** A speaker's tone, derived from the cluster id the same deterministic way a
 *  project's dot is derived from its site id — stable across reloads, no
 *  stored field, and no chance of two voices sharing a colour by accident of
 *  render order. */
function speakerTone(speakerId: string): string {
  return projectDot(speakerId);
}

/**
 * One transcript line.
 *
 * The left gutter is a wall-clock time (`clockOf`), NOT an offset into the
 * recording — an offset is a seek position, and there is nothing to seek.
 */
/** A padlock, at text size. The whole of the owner-only notice's ornament. */
function LockGlyph() {
  return (
    <svg
      width="12"
      height="14"
      viewBox="0 0 12 14"
      fill="none"
      stroke={C.sub2}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <rect x="1" y="6" width="10" height="7" rx="2" />
      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" />
    </svg>
  );
}

/**
 * The design's section heading, verbatim: mono 10px, letter-spacing 0.12em,
 * uppercase, weight 600, ink at 84% — used by every card on this page
 * (Photos, Items produced, Summary, Transcript). Built locally rather than
 * through `chrome.tsx`'s `Eyebrow` because `Eyebrow` is pinned to the
 * console's own type scale (9.5px/700/`C.ink`), which is a different, and
 * deliberately not this, palette (see the spec's colour note).
 */
const SECTION_HEADING_STYLE: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 11.5,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  // 700, not the artboard's 600 — a deliberate, user-directed deviation
  // (2026-09-07). At 10px, letterspaced, in JetBrains Mono, 600 reads thin
  // against the 34px/800 heading above it and the card's own weight.
  //
  // ONE constant for all four cards. Bolding only Photos, which is where it
  // was noticed, would leave four headings at two weights — which reads as
  // an oversight rather than a decision.
  fontWeight: 700,
  color: "rgba(10,10,10,.84)",
};

/** The head's fact row — Started / Ended / Duration / By — label and value,
 *  verbatim from the spec. */
const FACT_LABEL_STYLE: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10.93,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(10,10,10,.45)",
};
const FACT_VALUE_STYLE: React.CSSProperties = {
  fontSize: 21.85,
  fontWeight: 700,
  letterSpacing: "-0.02em",
  whiteSpace: "nowrap",
};
/** The right-hand stat band's own type — bigger and heavier than a fact. */
const STAT_VALUE_STYLE: React.CSSProperties = {
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: "-0.03em",
  lineHeight: 1,
};
const STAT_LABEL_STYLE: React.CSSProperties = { ...FACT_LABEL_STYLE };
/** A card's "nothing to show" line — Photos', Items produced's and the
 *  Transcript's empty states all read at this size per the spec. */
const EMPTY_ROW_STYLE: React.CSSProperties = {
  fontSize: 17.25,
  lineHeight: 1.5,
  color: "rgba(10,10,10,.52)",
};

/**
 * The design's card shell, verbatim — white ground, 16px radius, a 7.5%
 * hairline and the design's own one-pixel shadow. Distinct from `chrome.tsx`'s
 * `Card` defaults (`C.surface`/14px/`C.hair`/two-layer shadow), which are a
 * different palette; passed as this component's `style` override rather than
 * editing `Card` itself (out of scope — see the design report).
 */
const CARD_SHELL_STYLE: React.CSSProperties = {
  background: "white",
  border: "1px solid rgba(10,10,10,.075)",
  borderRadius: 16,
  boxShadow: "0 1px 2px rgba(10,10,10,.035)",
};

/** The design's back arrow — a plain chevron, rotated 180° by its caller for
 *  "Back". */
function BackArrowGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

function SectionHeading({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <span style={{ ...SECTION_HEADING_STYLE, display: "flex", alignItems: "center", gap: 7 }}>
        {children}
      </span>
      {right}
    </div>
  );
}

/** The bolt beside "Summary" — the design's own glyph, at its own size and
 *  amber (`var(--amber)`, hex B0/7A/00, written as its rgb() equivalent per
 *  `tokens.test.ts`'s hex-literal guard). */
function BoltGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="rgb(176,122,0)" aria-hidden>
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  );
}

/** The Items-produced row's icon box: the same per-kind glyph `KindChip`
 *  draws, at the design's own size and ink rather than the chip's — a plain
 *  mark in a bordered square, not a labelled pill (the design does not put
 *  the kind's NAME on this row; the icon is the only kind signal). */
function ItemIcon({ kind }: { kind: ConsoleItemKind }) {
  const icon = KIND_ICON[kind];
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="rgba(10,10,10,.5)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={icon.d1} />
      <path d={icon.d2} />
    </svg>
  );
}

function TranscriptLine({
  line,
  href,
  onOpen,
  showItemLink,
  itemKindLabel,
}: {
  line: WalkTranscriptLineResponse;
  href: string;
  onOpen: () => void;
  showItemLink: boolean;
  itemKindLabel?: string;
}) {
  const attributed = !!line.speakerId;
  return (
    <div
      data-testid="walk-transcript-line"
      data-line-id={line.id}
      style={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0,1fr)",
        gap: 14,
        padding: "18px 0",
        // The design rules under EVERY line, closing the list rather than
        // only separating its members — so the last sentence has a foot and
        // does not run into the card's padding.
        borderBottom: "1px solid rgba(10,10,10,.06)",
        alignItems: "start",
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12.65,
          color: "rgba(10,10,10,.38)",
          paddingTop: 3,
        }}
      >
        {clockOf(new Date(line.at))}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <span
          style={{
            fontSize: 16.1,
            fontWeight: 600,
            color: attributed ? speakerTone(line.speakerId!) : "rgba(10,10,10,.38)",
          }}
        >
          {line.speakerLabel ?? "Unattributed"}
        </span>
        <span
          style={{
            fontSize: 19.55,
            lineHeight: 1.45,
            color: attributed ? "rgba(10,10,10,.84)" : "rgba(10,10,10,.38)",
            textWrap: "pretty" as React.CSSProperties["textWrap"],
          }}
        >
          {line.text}
          {/* The record this sentence produced, as the design draws it: a
              small mono tag at the END of the words, naming the KIND.

              It used to be a full record card above the line — the kind chip
              and the whole title in an amber box. Every annotated line got
              one, which turned a reading surface into a stack of cards with
              sentences between them. The design annotates the prose instead.

              Still a link, and still carrying the record's title: the title
              moves to `title`/`aria-label` rather than being dropped, so the
              affordance and the name survive a treatment that no longer has
              room to print them. */}
          {showItemLink && line.itemId && (
            <a
              href={href}
              data-testid="walk-transcript-item"
              data-item-id={line.itemId}
              title={line.itemTitle}
              aria-label={
                line.itemTitle
                  ? `${itemKindLabel ?? "Record"}: ${line.itemTitle}`
                  : (itemKindLabel ?? "Record")
              }
              onClick={(event) => {
                event.preventDefault();
                onOpen();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                marginLeft: 8,
                verticalAlign: "middle",
                fontFamily: FONT_MONO,
                fontSize: 10.35,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "rgba(10,10,10,.52)",
                padding: "2px 6px",
                borderRadius: 4,
                background: "rgba(247,197,43,.25)",
                whiteSpace: "nowrap",
              }}
            >
              {itemKindLabel ?? (line.itemKind ? KIND_LABEL[line.itemKind] : "Record")}
            </a>
          )}
        </span>
      </div>
    </div>
  );
}

/** A stable, one-paragraph synopsis above the source transcript. The preview
 * stays the same height on first read so the walk's top cards align; More is
 * intentionally an explicit choice to trade that tidy scan for full detail. */
function WalkSummary({ summary }: { summary: string }) {
  const [expanded, setExpanded] = useState(false);
  const normalized = summary.replace(/\s+/g, " ").trim();
  // Three lines can clip a much shorter summary on the narrower walk layout.
  // Keep the affordance available before the line clamp becomes visible so a
  // reviewer never sees an ellipsis with no way to read the rest.
  const canExpand = normalized.length > 180;
  const toggleExpanded = () => setExpanded((value) => !value);
  const summaryStyle = {
    margin: "14px 0 0",
    minHeight: 60,
    padding: 0,
    border: "none",
    background: "transparent",
    width: "100%",
    textAlign: "left" as const,
    fontFamily: "inherit",
    fontSize: 19.55,
    lineHeight: 1.55,
    color: "rgba(10,10,10,.62)",
    textWrap: "pretty" as React.CSSProperties["textWrap"],
    cursor: canExpand ? "pointer" : "text",
    ...(expanded
      ? {}
      : {
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical" as const,
        }),
  };

  return (
    <div data-testid="walk-summary" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <SectionHeading>
            Summary
            <BoltGlyph />
          </SectionHeading>
          {canExpand && (
            <button
              type="button"
              data-testid="walk-summary-more"
              className="kb"
              aria-expanded={expanded}
              onClick={toggleExpanded}
              style={{
                flexShrink: 0,
                padding: 0,
                border: "none",
                background: "transparent",
                color: C.gold,
                fontSize: 13.8,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {expanded ? "Less" : "More"}
            </button>
          )}
        </div>
        {canExpand ? (
          <button
            type="button"
            data-testid="walk-summary-content"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse summary" : "Expand summary"}
            onClick={toggleExpanded}
            style={summaryStyle}
          >
            {normalized}
          </button>
        ) : (
          <p style={summaryStyle}>{normalized}</p>
        )}
      </div>
      <div style={{ height: 1, background: "rgba(10,10,10,.06)" }} />
    </div>
  );
}

/** A captured photo or video frame, as the design's tile: the capture time
 *  stamped on the image, the DESCRIPTION revealed on hover (never the
 *  caption — that names the downloaded file), and the whole tile opening the
 *  viewer. There is no player and no duration — a `video` row renders its
 *  poster tile, exactly as `EvidenceTile`'s own note says. */
function PhotoTile({
  photo,
  index,
  visitId,
  ticket,
  onOpen,
}: {
  photo: WalkPhotoResponse;
  index: number;
  visitId: string;
  ticket: string | null;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const name = `${String(index + 1).padStart(2, "0")} ${photo.caption ?? photo.description ?? "photo"}`.trim();
  /** With a ticket the URL authorizes itself, which is what a drag needs and
   *  what lets a download stream to disk. Without one the same route still
   *  answers a bearer `fetch`. */
  const fileUrl = photoFileUrl(visitId, photo.id, ticket);

  return (
    <span
      data-testid="walk-photo"
      data-photo-id={photo.id}
      data-draggable={ticket ? "file" : "url"}
      className="wtile"
      role="button"
      tabIndex={0}
      aria-label={`Open photo ${index + 1}`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      // The design's tile border is `rgba(10,10,10,.06)` at radius 10; the
      // `EvidenceTile` this wraps draws its OWN 1px border and radius-11
      // internally (it is shared chrome, out of scope here — see the design
      // report), so this wrapper does not add a second ring on top of it.
      style={{ display: "block", position: "relative", borderRadius: 10, overflow: "hidden", cursor: "pointer", minWidth: 0 }}
      // Dragged out of the page as a FILE where the browser supports it
      // (Chromium's `DownloadURL`), and as the image's URL everywhere else —
      // see `lib/photoTransfer.ts`, which also says why this cannot await a
      // ticket it does not already have.
      draggable
      onDragStart={(event) => {
        setDragFile(event.dataTransfer, {
          url: ticket ? fileUrl : photo.url ?? fileUrl,
          name: `${name}.jpg`,
          mimeType: photo.type === "video" ? "video/mp4" : "image/jpeg",
        });
      }}
    >
      <EvidenceTile
        jobSiteId={photo.id}
        url={photo.thumbnailUrl ?? photo.url}
        height={110}
        stamp={clockOf(new Date(photo.capturedAt))}
        alt={photo.description ?? ""}
      />
      {/* The hover veil: the DESCRIPTION, and the tile's own download. The
          description, NOT the caption: they are different fields saying
          different things, and the caption's job here is to name the
          downloaded file. A photo with no description yet reveals only the
          download. */}
      <span
        className="wcap"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "flex-end",
          gap: 8,
          padding: "8px 9px",
          background: "linear-gradient(180deg, rgba(10,10,10,0) 40%, rgba(10,10,10,0.82))",
        }}
      >
        {photo.description && (
          <span
            data-testid="walk-photo-description"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13.23,
              fontWeight: 600,
              lineHeight: 1.35,
              color: C.surface,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {photo.description}
          </span>
        )}
        <button
          className="kb"
          data-testid="walk-photo-download"
          data-photo-id={photo.id}
          aria-label={`Download photo ${index + 1}`}
          title={failed ? "Download failed" : `Download photo ${index + 1}`}
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            const ok = await downloadFile(fileUrl, `${name}.jpg`, { authenticated: !ticket });
            setFailed(!ok);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginLeft: "auto",
            height: 22,
            padding: "0 5px",
            border: "1px solid transparent",
            borderRadius: 7,
            background: "transparent",
            color: failed ? C.red : C.surface,
            cursor: "pointer",
          }}
        >
          <DownloadGlyph />
        </button>
      </span>
    </span>
  );
}

/**
 * The photos card's hand-off controls: download every photo as one zip, and
 * drag the set out as that same zip.
 *
 * The zip is a SERVER build (`getWalkPhotoFiles.ts`): the photo URLs are
 * cross-origin presigned R2 GETs, so the page cannot fetch them to zip them,
 * and N separate downloads trip Chrome's multiple-download prompt. One file,
 * one click, named after the walk.
 *
 * "Drag all" is the same zip as a drag rather than a click, for the case the
 * whole feature is for: Procore is open in the next window and the person
 * would rather not visit their Downloads folder. Whether a given drop target
 * takes a file is that target's decision — which is exactly why the download
 * button sits beside it and not behind it.
 */
function PhotosHandoff({
  visitId,
  walkName,
  photos,
  ticket,
}: {
  visitId: string;
  walkName: string;
  photos: WalkPhotoResponse[];
  ticket: string | null;
}) {
  const [state, setState] = useState<"idle" | "working" | "failed">("idle");
  const zipUrl = photosZipUrl(visitId, ticket);
  const zipName = `${walkName} photos.zip`;

  return (
    <>
      <button
        className="kb"
        data-testid="walk-photos-download-all"
        aria-label={`Download all ${photos.length} photos`}
        title={
          state === "failed"
            ? "Download failed — try one photo at a time"
            : `Download all ${photos.length} photos as a zip`
        }
        onClick={async () => {
          setState("working");
          const ok = await downloadFile(zipUrl, zipName, { authenticated: !ticket });
          setState(ok ? "idle" : "failed");
        }}
        // The same drag as the tiles, carrying the whole set: one zip, because
        // a `DownloadURL` drag transfers exactly one file.
        draggable={ticket !== null}
        onDragStart={(event) => {
          if (!ticket) return;
          setDragFile(event.dataTransfer, {
            url: zipUrl,
            name: zipName,
            mimeType: "application/zip",
          });
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 20,
          padding: 0,
          border: "none",
          background: "transparent",
          color: state === "failed" ? C.red : "rgb(176,122,0)",
          cursor: state === "working" ? "progress" : "pointer",
          font: "inherit",
        }}
      >
        <DownloadGlyph size={12} />
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11.5,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: "inherit",
          }}
        >
          {state === "working" ? "ZIPPING…" : state === "failed" ? "FAILED" : "ALL"}
        </span>
      </button>
    </>
  );
}

/** A tray with an arrow into it — the same glyph the download-all button
 *  uses, so one affordance is recognizable at two sizes. */
function DownloadGlyph({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 4v10" />
      <path d="M8 10.5l4 3.9 4-3.9" />
      <path d="M5 19h14" />
    </svg>
  );
}

/** The viewer rail's DESCRIPTION section — reading view, and (for the walk's
 *  own walker) the design's EDIT: a textarea over the same field the phone's
 *  caption editor writes (`updatePhotoCaption` → `userCaption`, which
 *  overrides the AI description on every surface including Procore). */
function ViewerDescription({
  photo,
  visitId,
  search,
  canEdit,
  railLabel,
  onSaved,
}: {
  photo: WalkPhotoResponse;
  visitId: string;
  search: string;
  canEdit: boolean;
  railLabel: React.CSSProperties;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  /** Uncontrolled, read on Save — the codebase's textarea idiom (see the
   *  Policies personal-instructions field): no per-keystroke state, and the
   *  test harness's synthetic typing works against the DOM value. */
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const [state, setState] = useState<"idle" | "saving" | "failed" | "empty">("idle");

  const save = async () => {
    if (state === "saving") return;
    const text = (draftRef.current?.value ?? "").trim();
    if (!text) {
      setState("empty");
      return;
    }
    setState("saving");
    const result = await updateWalkPhotoDescription(visitId, photo.id, search, text);
    if (result.ok) {
      setState("idle");
      setEditing(false);
      onSaved();
    } else {
      setState("failed");
    }
  };

  return (
    <div style={{ padding: "26px 26px 22px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ ...railLabel, color: C.gold }}>DESCRIPTION</span>
        {canEdit && !editing && (
          <button
            className="kb"
            data-testid="viewer-description-edit"
            onClick={() => {
              setState("idle");
              setEditing(true);
            }}
            style={{ ...railLabel, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
          >
            EDIT
          </button>
        )}
      </div>
      {editing ? (
        <div style={{ marginTop: 12 }}>
          <textarea
            data-testid="viewer-description-input"
            ref={draftRef}
            defaultValue={photo.description ?? ""}
            rows={4}
            maxLength={4000}
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(255,255,255,0.06)",
              color: C.surface,
              font: "inherit",
              fontSize: 16.1,
              lineHeight: 1.5,
              padding: "10px 12px",
            }}
          />
          {(state === "failed" || state === "empty") && (
            <div style={{ fontSize: 13.8, color: C.red, marginTop: 6 }}>
              {state === "empty"
                ? "A description can\u2019t be empty."
                : "The edit did not save — try again."}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button
              className="kb"
              data-testid="viewer-description-save"
              onClick={() => void save()}
              disabled={state === "saving"}
              style={{
                height: 32,
                padding: "0 14px",
                borderRadius: 10,
                border: "none",
                background: C.gold,
                color: "rgba(0,0,0,0.85)",
                fontSize: 14.37,
                fontWeight: 700,
                cursor: state === "saving" ? "progress" : "pointer",
                font: "inherit",
              }}
            >
              {state === "saving" ? "Saving…" : "Save"}
            </button>
            <button
              className="kb"
              onClick={() => setEditing(false)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "transparent",
                color: C.surface,
                fontSize: 14.37,
                fontWeight: 600,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div
          data-testid="viewer-description"
          style={{ fontSize: 18.4, fontWeight: 500, lineHeight: 1.55, marginTop: 12, textWrap: "pretty" }}
        >
          {photo.description ?? "No description yet — the walk is still being processed, or this photo did not get one."}
        </div>
      )}
    </div>
  );
}

/** The viewer rail's COMMENTS: the photo's thread (org-visible, same ceiling
 *  as the photo itself) and a composer that posts through `backend/`. */
function ViewerComments({
  visitId,
  photoId,
  search,
  railLabel,
}: {
  visitId: string;
  photoId: string;
  search: string;
  railLabel: React.CSSProperties;
}) {
  const [comments, setComments] = useState<PhotoCommentResponse[] | null>(null);
  const [failed, setFailed] = useState(false);
  /** Uncontrolled — see ViewerDescription's note. An empty send is a no-op. */
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const [posting, setPosting] = useState<"idle" | "working" | "failed">("idle");
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchPhotoComments(visitId, photoId, search)
      .then((response) => {
        if (cancelled) return;
        setComments(Array.isArray(response.comments) ? response.comments : []);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, photoId, search, generation]);

  const post = async () => {
    if (posting === "working") return;
    const text = (draftRef.current?.value ?? "").trim();
    if (!text) return;
    setPosting("working");
    const result = await addPhotoComment(visitId, photoId, search, text);
    if (result.ok) {
      if (draftRef.current) draftRef.current.value = "";
      setPosting("idle");
      setGeneration((count) => count + 1);
    } else {
      setPosting("failed");
    }
  };

  return (
    <div
      data-testid="viewer-comments"
      style={{ padding: "22px 26px", flex: 1, minHeight: 0, overflowY: "auto" }}
    >
      <span style={railLabel}>COMMENTS</span>
      {failed ? (
        <div style={{ fontSize: 14.95, color: "rgba(255,255,255,0.45)", marginTop: 12 }}>
          Comments did not load — close and reopen the photo to retry.
        </div>
      ) : comments === null ? (
        <div style={{ fontSize: 14.95, color: "rgba(255,255,255,0.45)", marginTop: 12 }}>Loading…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
          {comments.map((comment) => (
            <div key={comment.id} data-testid="viewer-comment">
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 14.37, fontWeight: 700 }}>
                  {comment.mine ? "You" : comment.authorName ?? "Teammate"}
                </span>
                <span style={{ ...railLabel, fontSize: 9.77 }}>{whenStamp(comment.at)}</span>
              </div>
              <div style={{ fontSize: 16.1, fontWeight: 500, lineHeight: 1.5, marginTop: 3 }}>
                {comment.text}
              </div>
            </div>
          ))}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          marginTop: 14,
          padding: "12px 12px 10px 14px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <textarea
          data-testid="viewer-comment-input"
          ref={draftRef}
          placeholder="Add a comment…"
          rows={2}
          maxLength={4000}
          style={{
            flex: 1,
            minWidth: 0,
            resize: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            color: C.surface,
            font: "inherit",
            fontSize: 16.1,
            lineHeight: 1.5,
          }}
        />
        <button
          className="kb"
          data-testid="viewer-comment-send"
          aria-label="Send comment"
          onClick={() => void post()}
          disabled={posting === "working"}
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            border: "none",
            background: C.gold,
            color: "rgba(0,0,0,0.85)",
            cursor: posting === "working" ? "progress" : "pointer",
            fontSize: 16.1,
            flexShrink: 0,
          }}
        >
          ↑
        </button>
      </div>
      {posting === "failed" && (
        <div style={{ fontSize: 13.8, color: C.red, marginTop: 6 }}>
          The comment did not post — try again.
        </div>
      )}
    </div>
  );
}

/**
 * The design's photo viewer: the photo at reading size on a dark ground, with
 * the walk's account of it — description, capture facts, the file's own
 * download — in a rail beside it.
 *
 * EDIT (walker-only, `ViewerDescription`) lands on the mobile manager's own
 * `updatePhotoCaption` through the console's PATCH route; COMMENTS
 * (`ViewerComments`) read console-side and post through `backend/`.
 *
 * A `video` row shows its poster frame — never a `<video>` element (spec
 * §7.3; the page-level test forbids the tag).
 */
function PhotoViewer({
  photos,
  index,
  walk,
  visitId,
  ticket,
  search,
  canEdit,
  onDescriptionSaved,
  onStep,
  onClose,
}: {
  photos: WalkPhotoResponse[];
  index: number;
  walk: WalkHeaderResponse;
  visitId: string;
  ticket: string | null;
  search: string;
  /** The walk's own walker may edit a description — the same owner rule the
   *  phone's caption editor enforces, mirrored so the control never 404s. */
  canEdit: boolean;
  onDescriptionSaved: () => void;
  onStep: (next: number) => void;
  onClose: () => void;
}) {
  const photo = photos[index]!;
  const signedInPerson = useAuthPerson();
  const [annotating, setAnnotating] = useState(false);
  const count = photos.length;
  const name = `${String(index + 1).padStart(2, "0")} ${photo.caption ?? photo.description ?? "photo"}`.trim();
  const fileUrl = photoFileUrl(visitId, photo.id, ticket);
  const [failed, setFailed] = useState(false);
  const narrow = useMediaQuery("(max-width: 820px)");

  /** Wraps at the ends — the count above the photo states the whole set, so
   *  the reader has no end to need. */
  const prev = () => onStep((index + count - 1) % count);
  const next = () => onStep((index + 1) % count);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (annotating) return;
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft") prev();
      else if (event.key === "ArrowRight") next();
    };
    addEventListener("keydown", onKey);
    // The page under the overlay must not scroll while it is up.
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      removeEventListener("keydown", onKey);
      document.body.style.overflow = held;
    };
  });

  const railLabel: React.CSSProperties = {
    ...META,
    fontSize: 10.93,
    letterSpacing: "1.4px",
    color: "rgba(255,255,255,0.45)",
  };
  const meta: Array<{ k: string; v: string }> = [
    { k: "Taken on", v: `${dayLabel(photo.capturedAt)} · ${clockOf(new Date(photo.capturedAt))}` },
    { k: "Captured by", v: !walk.creatorName?.trim() && canEdit
      ? signedInPerson?.name?.trim() || signedInPerson?.email?.trim() || walkerLabel(walk)
      : walkerLabel(walk) },
    { k: "Walk", v: walk.name || "Untitled walk" },
    ...(walk.siteName ? [{ k: "Project", v: walk.siteName }] : []),
    { k: "Location", v: photo.location?.path || photo.location?.name || "Not set" },
  ];

  return createPortal(
    <div
      data-testid="walk-photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${count}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: narrow ? "column" : "row",
        background: C.viewerBg,
      }}
    >
      {annotating && <PhotoAnnotationEditor
        imageUrl={fileUrl}
        visitId={visitId}
        photoId={photo.id}
        search={search}
        onCancel={() => setAnnotating(false)}
        onSaved={() => { setAnnotating(false); onDescriptionSaved(); }}
      />}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          className="kb wvbtn"
          data-testid="viewer-close"
          onClick={onClose}
          style={{
            position: "absolute",
            left: 22,
            top: 20,
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 36,
            padding: "0 14px",
            borderRadius: 999,
            border: "none",
            color: C.surface,
            cursor: "pointer",
            zIndex: 1,
          }}
        >
          <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.3px", color: "inherit" }}>✕ CLOSE</span>
        </button>
        <span
          data-testid="viewer-count"
          style={{
            ...META,
            position: "absolute",
            left: 0,
            right: 0,
            top: 28,
            textAlign: "center",
            fontSize: 11.5,
            letterSpacing: "1.4px",
            color: "rgba(255,255,255,0.45)",
          }}
        >
          {`${index + 1} / ${count}`}
        </span>
        {count > 1 && (
          <>
            <button
              className="kb wvbtn"
              data-testid="viewer-prev"
              aria-label="Previous photo"
              onClick={prev}
              style={{ position: "absolute", left: 24, top: "50%", marginTop: -24, width: 48, height: 48, borderRadius: 24, border: "none", color: C.surface, cursor: "pointer", zIndex: 1, fontSize: 18.4 }}
            >
              ‹
            </button>
            <button
              className="kb wvbtn"
              data-testid="viewer-next"
              aria-label="Next photo"
              onClick={next}
              style={{ position: "absolute", right: 24, top: "50%", marginTop: -24, width: 48, height: 48, borderRadius: 24, border: "none", color: C.surface, cursor: "pointer", zIndex: 1, fontSize: 18.4 }}
            >
              ›
            </button>
          </>
        )}
        {photo.url || photo.thumbnailUrl ? (
          <PhotoImage
            src={photo.type === "video" ? photo.thumbnailUrl ?? "" : photo.url ?? photo.thumbnailUrl ?? ""}
            alt={photo.description ?? ""}
            style={{
              maxWidth: "calc(100% - 144px)",
              maxHeight: "86vh",
              objectFit: "contain",
              borderRadius: 6,
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
          />
        ) : (
          <span style={{ ...META, fontSize: 11.5, color: "rgba(255,255,255,0.45)" }}>
            NO PREVIEW
          </span>
        )}
      </div>
      <div
        style={{
          flex: "none",
          width: narrow ? "100%" : 360,
          background: C.viewerRail,
          borderLeft: narrow ? "none" : "1px solid rgba(255,255,255,0.07)",
          color: C.surface,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <ViewerDescription
          key={`d-${photo.id}`}
          photo={photo}
          visitId={visitId}
          search={search}
          canEdit={canEdit}
          railLabel={railLabel}
          onSaved={onDescriptionSaved}
        />
        <div style={{ padding: "22px 26px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <span style={railLabel}>GENERAL</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
            {meta.map((row) => (
              <div key={row.k} data-testid="viewer-meta" data-k={row.k}>
                <div style={{ fontSize: 13.8, fontWeight: 500, color: "rgba(255,255,255,0.45)" }}>{row.k}</div>
                <div style={{ fontSize: 16.1, fontWeight: 600, marginTop: 3, overflowWrap: "anywhere" }}>{row.v}</div>
              </div>
            ))}
          </div>
        </div>
        <ViewerComments key={`c-${photo.id}`} visitId={visitId} photoId={photo.id} search={search} railLabel={railLabel} />
        <div style={{ padding: "16px 26px 22px" }}>
          {canEdit && photo.type === "image" && <button
            className="kb"
            data-testid="viewer-annotate"
            onClick={() => setAnnotating(true)}
            style={{ width: "100%", height: 44, marginBottom: 10, borderRadius: 12, border: "none", background: C.gold, color: C.ink, font: "inherit", cursor: "pointer" }}
          >Annotate</button>}
          <button
            className="kb"
            data-testid="viewer-download"
            onClick={async () => {
              const ok = await downloadFile(fileUrl, `${name}.jpg`, { authenticated: !ticket });
              setFailed(!ok);
            }}
            style={{
              width: "100%",
              height: 44,
              borderRadius: 12,
              border: `1px solid ${failed ? C.red : "rgba(255,255,255,0.18)"}`,
              background: "transparent",
              color: failed ? C.red : C.surface,
              fontSize: 15.52,
              fontWeight: 700,
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {failed ? "Download failed — try again" : "Download"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const WalkNameEditor = createWalkNameEditor(WalkEditorReact);

export function WalkDetail(
  props: ConsoleViewProps & { visitId: string; backHref: string; onBack: () => void },
) {
  const {
    visitId,
    backHref,
    onBack,
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForItem,
    onOpenItem,
    hrefForProject,
    onOpenProject,
  } = props;

  /** Bumped after every landed decision — the page re-reads rather than
   *  patching a row optimistically. See `ReviewQueue.tsx`. */
  const [decisions, setDecisions] = useState(0);

  /** The photo viewer's open index — `null` when the page is the page. */
  const [viewer, setViewer] = useState<number | null>(null);
  const [inlinePhoto, setInlinePhoto] = useState<FullscreenPhoto | null>(null);

  const { data, failure, refreshFailure } = useResource<WalkDetailResponse>(
    `walk:${visitId}${resourceQuery(search, ["workspace"])}`,
    () => fetchWalkDetail(visitId, search),
    refreshKey + decisions,
  );

  /** Stacked on a phone: two columns at 390px would put a transcript in a
   *  60-character gutter. The ORDER is the desktop order — photos and records
   *  first, transcript under them.
   *
   *  No `narrow` flag lives here any more: the two-column grid below mirrors
   *  the design artboard's `auto-fit` rule, which collapses on its own when a
   *  track cannot reach 340px. One rule instead of a rule plus a matching JS
   *  breakpoint that had to be kept in step with it by hand. */
  /** Asked for as soon as the page opens; see `usePhotoTicket`. Only a walk
   *  with photos needs one. */
  const ticket = usePhotoTicket(visitId, search, (data?.photos.length ?? 0) > 0);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <LoadingPanel shape="walk" />;

  const { walk, review } = data;
  const pendingGroup = review.groups.find((group) => group.id === "not_reviewed");
  const pending = pendingGroup?.total ?? 0;
  const pendingRows = pendingGroup?.sites.flatMap((site) => site.rows) ?? [];

  const scopeName = walk.siteName || walk.jobSiteId;

  return (
    <>
      {/* The design's own top bar: 52px, mono breadcrumb type throughout.
          The sync pill and the overflow (⋯) button the artboard draws here
          are NOT built — neither has a backing field on `WalkHeaderResponse`
          (no sync status, no defined overflow action), and inventing either
          would be new functionality dressed as a restyle. See the design
          report. */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 52,
          padding: "0 48px",
          borderBottom: "1px solid rgba(10,10,10,.06)",
          fontFamily: FONT_MONO,
          fontSize: 11.5,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "rgba(10,10,10,.45)",
        }}
      >
        <a
          href={backHref}
          data-testid="walk-back"
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "rgba(10,10,10,.72)",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
            <BackArrowGlyph />
          </span>
          Back
        </a>
        <span style={{ width: 1, height: 16, background: "rgba(10,10,10,.1)" }} />
        <a
          href={hrefForProject(walk.jobSiteId)}
          data-testid="walk-project-link"
          onClick={(event) => {
            event.preventDefault();
            onOpenProject(walk.jobSiteId);
          }}
          style={{
            color: "inherit",
            textDecoration: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {`${scopeName} · Walks`}
        </a>
      </div>

      {/* The scrolling body — everything below the top bar, including the
          head, is one column per the design (`padding:36px 48px 56px;
          max-width:1120px`). The bottom clearance is the shell's fixed Ask
          composer's own reserved space (`ASK_COMPOSER_CLEARANCE`), layered
          under the design's own 56px so the last card never rests on the
          composer's edge. */}
      <div className="sb" style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `0 0 ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)` }}>
        <div
          style={{
            padding: "36px 48px 56px",
            maxWidth: 1120,
            width: "100%",
            boxSizing: "border-box",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* HEAD */}
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <div>
              <div style={{ ...FACT_LABEL_STYLE, marginBottom: 10 }}>
                {dayLabel(walk.startedAt)} <span style={{ color: "rgba(10,10,10,.25)" }}>·</span> {scopeName}
              </div>
              <WalkNameEditor key={`${data.userId}:${visitId}:${search}`} snapshot={walk} editable={data.viewerIsOwner}
                onSave={async name => {
                  const response = await postMutation<WalkNameSnapshot>(`/api/walks/${encodeURIComponent(visitId)}/name`, search, { name });
                  if (!response.ok || !response.result) throw new Error(response.error || "Could not rename walk");
                  setDecisions(value => value + 1);
                  return response.result;
                }}
                style={{ margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 }} />
            </div>
            {/* The facts row — Started · Ended · Duration · By — with the
                walk's three counts banded on the right. The walk's OWN times,
                not the audio's: see the file header. */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 36, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 36, flexWrap: "wrap", flex: 1 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={FACT_LABEL_STYLE}>Started</span>
                  <span data-testid="walk-started-at" style={FACT_VALUE_STYLE}>
                    {clockOf(new Date(walk.startedAt))}
                  </span>
                </div>
                {walk.endedAt && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={FACT_LABEL_STYLE}>Ended</span>
                    <span data-testid="walk-ended-at" style={FACT_VALUE_STYLE}>
                      {clockOf(new Date(walk.endedAt))}
                    </span>
                  </div>
                )}
                {walk.durationMinutes !== undefined && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={FACT_LABEL_STYLE}>Duration</span>
                    <span data-testid="walk-duration" style={FACT_VALUE_STYLE}>
                      {`${walk.durationMinutes} min`}
                    </span>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={FACT_LABEL_STYLE}>By</span>
                  <span style={FACT_VALUE_STYLE}>{walkerLabel(walk)}</span>
                </div>
              </div>
              <div data-testid="walk-statband" style={{ display: "flex" }}>
                {[
                  { value: String(data.photos.length), label: "Photos" },
                  { value: String(data.items.length), label: "Items" },
                  {
                    value: data.transcriptOwnerOnly ? "—" : String(data.transcript.length),
                    label: "Lines",
                  },
                ].map((stat, statIndex) => (
                  <div
                    key={stat.label}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "0 24px",
                      borderLeft: statIndex === 0 ? "none" : "1px solid rgba(10,10,10,.1)",
                    }}
                  >
                    <span style={STAT_VALUE_STYLE}>{stat.value}</span>
                    <span style={STAT_LABEL_STYLE}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

          {pending > 0 && (
            <Card testId="walk-review">
              <SectionHeading
                right={
                  <span style={{ ...META, fontSize: 9.77, color: C.sub2 }}>
                    {`${pending} of ${data.items.length} left`}
                  </span>
                }
              >
                Pending review
              </SectionHeading>
              <div style={{ marginTop: 8 }} />
            <a data-testid="walk-open-review" href={reviewRecordHref(props.hrefForReview(walk.jobSiteId), pendingRows[0]!.item)}
              onClick={event => { event.preventDefault(); props.onNavigate(reviewRecordHref(props.hrefForReview(walk.jobSiteId), pendingRows[0]!.item)); }}
              style={{ display: "inline-block", marginBottom: 12, color: C.goldInk, fontWeight: 650 }}>
              {pending} {pending === 1 ? "item" : "items"} ready for review — open review
            </a>
            {!review.writesConfigured && (
              <div
                data-testid="walk-review-note"
                style={{ fontSize: 14.95, color: C.sub, lineHeight: 1.5, marginBottom: 10 }}
              >
                {review.writesNote}
              </div>
            )}
            {pendingRows.map((row, index) => (
                <div
                  key={row.item.id}
                  data-testid="walk-review-row"
                  data-item-id={row.item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 0",
                    borderTop: index === 0 ? "1px solid transparent" : `1px solid ${C.line}`,
                  }}
                >
                  <button
                    type="button"
                    className="kb"
                    aria-label={`Open photo for ${row.item.title}`}
                    onClick={() => {
                      if (!row.item.thumbnailUrl) return;
                      setInlinePhoto({
                        id: row.item.id,
                        url: row.item.thumbnailUrl,
                        thumbnailUrl: row.item.thumbnailUrl,
                        description: row.item.title,
                        siteName: data.walk.siteName,
                      });
                    }}
                    style={{ width: 36, flexShrink: 0, cursor: row.item.thumbnailUrl ? "zoom-in" : "default" }}
                  >
                    <EvidenceTile
                      jobSiteId={row.item.jobSiteId}
                      url={row.item.thumbnailUrl}
                      alt=""
                      height={36}
                    />
                  </button>
                  <a
                    href={reviewRecordHref(props.hrefForReview(walk.jobSiteId), row.item)}
                    onClick={(event) => {
                      event.preventDefault();
                      props.onNavigate(reviewRecordHref(props.hrefForReview(walk.jobSiteId), row.item));
                    }}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <span style={{ ...ROW_LINE, gap: 8 }}>
                      <KindChip kind={row.item.kind} label={mobileRecordKindLabel(row.item) ?? undefined} />
                      <span
                        data-testid="row-title"
                        style={{
                          ...ROW_TITLE,
                          fontSize: 16.67,
                          fontWeight: 650,
                          whiteSpace: "normal",
                          lineHeight: 1.35,
                        }}
                      >
                        {row.item.title}
                      </span>
                    </span>
                    <span
                      style={{
                        ...META,
                        display: "block",
                        marginTop: 4,
                        fontSize: 10.35,
                        fontWeight: 400,
                        letterSpacing: "0.4px",
                        color: C.sub2,
                      }}
                    >
                      {[whenStamp(row.item.createdAt), row.assignment?.provenance]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </a>
                  <a data-testid="walk-review-item" href={reviewRecordHref(props.hrefForReview(walk.jobSiteId), row.item)}
                    onClick={event => { event.preventDefault(); props.onNavigate(reviewRecordHref(props.hrefForReview(walk.jobSiteId), row.item)); }}
                    style={{ color: C.goldInk, fontSize: 14.37, fontWeight: 650 }}>Review item →</a>
                </div>
              ))}
          </Card>
          )}

          {/* Photos and produced records on the LEFT, transcript on the
              RIGHT (user-directed, 2026-08-31) — the photos and the records
              are what gets carried into Procore, so they are where the eye
              starts, and the transcript is the reference beside them.

              The two columns are EQUAL. They were 1 : 1.7 until the design
              artboard was mirrored (2026-09-07); the artboard draws them the
              same width, and `auto-fit` is how it does it — one rule that
              also collapses to a single column when there is less than 340px
              to give a track, so this element needs no `narrow` breakpoint of
              its own. See `.superpowers/design/walkdetail-spec.md`. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: WALK_COLUMNS,
              gap: 22,
              alignItems: "start",
            }}
          >
            <div
              data-testid="walk-side-column"
              style={{ display: "flex", flexDirection: "column", gap: 22 }}
            >
              <Card
                testId="walk-photos"
                style={{ ...CARD_SHELL_STYLE, padding: "22px 24px", display: "flex", flexDirection: "column", gap: 16 }}
              >
                <SectionHeading
                  right={
                    data.photos.length > 0 ? (
                      <PhotosHandoff
                        visitId={visitId}
                        walkName={walk.name || "Walk"}
                        photos={data.photos}
                        ticket={ticket}
                      />
                    ) : undefined
                  }
                >
                  {`Photos · ${data.photos.length}`}
                </SectionHeading>
                {data.photos.length === 0 ? (
                  <div style={EMPTY_ROW_STYLE}>No photos were captured on this walk.</div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                      gridAutoFlow: "row",
                      gap: 10,
                    }}
                  >
                    {data.photos.map((photo, index) => (
                      <PhotoTile
                        key={photo.id}
                        photo={photo}
                        index={index}
                        visitId={visitId}
                        ticket={ticket}
                        onOpen={() => setViewer(index)}
                      />
                    ))}
                  </div>
                )}
              </Card>

              <Card
                testId="walk-items"
                style={{ ...CARD_SHELL_STYLE, padding: "22px 24px", display: "flex", flexDirection: "column", gap: 14 }}
              >
                <SectionHeading>{`Items produced · ${data.items.length}`}</SectionHeading>
                {data.items.length === 0 ? (
                  <div style={EMPTY_ROW_STYLE}>
                    This walk produced no records. It still happened — a quiet walk is coverage,
                    not a gap.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {data.items.map((item, index) => (
                      <a
                        key={item.id}
                        href={hrefForItem(item.id)}
                        data-testid="walk-item"
                        data-item-id={item.id}
                        className="wrowh"
                        onClick={(event) => {
                          event.preventDefault();
                          onOpenItem(item.id);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          padding: "12px 0",
                          borderRadius: 8,
                          textDecoration: "none",
                          color: "inherit",
                          borderTop: index === 0 ? "1px solid transparent" : "1px solid rgba(10,10,10,.06)",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            border: "1px solid rgba(10,10,10,.08)",
                            flexShrink: 0,
                            marginTop: 1,
                          }}
                        >
                          <ItemIcon kind={item.kind} />
                        </span>
                        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                          <span
                            data-testid="row-title"
                            style={{
                              fontSize: 16.67,
                              fontWeight: 600,
                              lineHeight: 1.35,
                              textWrap: "pretty" as React.CSSProperties["textWrap"],
                            }}
                          >
                            {item.title}
                          </span>
                          <span style={{ fontSize: 14.37, color: "rgba(10,10,10,.52)" }}>
                            {`${mobileRecordKindLabel(item) ?? KIND_LABEL[item.kind]} · ${whenStamp(item.createdAt)}`}
                          </span>
                        </span>
                        {item.procoreSync?.status && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              fontFamily: FONT_MONO,
                              fontSize: 10.35,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "rgba(10,10,10,.52)",
                              whiteSpace: "nowrap",
                              paddingTop: 5,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: 3,
                                background: item.procoreSync.status === "synced" ? C.green : C.sub2,
                              }}
                            />
                            {humanize(item.procoreSync.status)}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <Card testId="walk-transcript" style={{ ...CARD_SHELL_STYLE, padding: "26px 28px", gap: 22, display: "flex", flexDirection: "column" }}>
              {data.summary && <WalkSummary summary={data.summary} />}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SectionHeading
                  right={
                    <span
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 11.5,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "rgba(10,10,10,.45)",
                      }}
                    >
                      {data.transcriptOwnerOnly ? "Private" : "Cross-talk removed"}
                    </span>
                  }
                >
                  Transcript
                </SectionHeading>
                {data.transcriptOwnerOnly ? (
                  /*
                    A padlock and four words, dressed in the design's own
                    panel (padding, radius, `C.inset` ground — the same hex
                    the design itself writes for this panel's background).

                    This was three sentences: who can read it, a line about
                    whose walk it was, and a pointer to the photos below. All
                    true, and all of it an essay in the slot where a STATE
                    belongs — the reader wants to know why the transcript is
                    not there, not to be argued with about it. The design's
                    OWN copy for this panel ("Only {name} can see the
                    transcript" plus a two-sentence body) was tried and
                    reverted for the same reason (see
                    `remainingViews.test.tsx`'s "a co-member is told the
                    transcript is the walker's" test) — kept here as the
                    established one-line copy, restyled to the design's
                    panel shape. See the design report for this call.
                  */
                  <div
                    data-testid="walk-transcript-owner-only"
                    title={`Only ${walk.creatorName || "the person who walked it"} can read this transcript.`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "18px 20px",
                      borderRadius: 12,
                      background: C.inset,
                      border: "1px solid rgba(10,10,10,.06)",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 999,
                        background: "white",
                        border: "1px solid rgba(10,10,10,.08)",
                        flexShrink: 0,
                      }}
                    >
                      <LockGlyph />
                    </span>
                    <span style={{ fontSize: 16.67, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                      Visible only to owner
                    </span>
                  </div>
                ) : data.transcript.length === 0 ? (
                  <div style={EMPTY_ROW_STYLE}>
                    This walk has no transcript. That happens when a walk was captured without
                    speech, or before diarization finished — the photos and records below are
                    unaffected.
                  </div>
                ) : (
                  data.transcript.map((line, index) => {
                    const item = line.itemId ? data.items.find((candidate) => candidate.id === line.itemId) : undefined;
                    return (
                      <TranscriptLine
                        key={line.id}
                        line={line}
                        href={line.itemId ? hrefForItem(line.itemId) : "#"}
                        onOpen={() => line.itemId && onOpenItem(line.itemId)}
                        showItemLink={
                          !!line.itemId &&
                          !data.transcript.slice(0, index).some((previous) => previous.itemId === line.itemId)
                        }
                        itemKindLabel={item ? mobileRecordKindLabel(item) ?? undefined : undefined}
                      />
                    );
                  })
                )}
              </div>
            </Card>
          </div>

          {/* "This walk captured nothing" is a claim about the WALK. A
              co-member whose transcript was withheld has not been shown
              enough to make it, so the panel is theirs to see only when the
              emptiness is real. */}
          {!data.transcriptOwnerOnly &&
            data.transcript.length === 0 &&
            data.photos.length === 0 &&
            data.items.length === 0 && (
              <EmptyPanel
                title="This walk captured nothing"
                body="No transcript, no photos and no records. The walk was recorded — there is simply nothing in it to show."
              />
            )}
        </div>
      </div>

      {viewer !== null && data.photos[viewer] && (
        <PhotoViewer
          photos={data.photos}
          index={viewer}
          walk={walk}
          visitId={visitId}
          ticket={ticket}
          search={search}
          canEdit={data.viewerIsOwner}
          onDescriptionSaved={() => setDecisions((count) => count + 1)}
          onStep={setViewer}
          onClose={() => setViewer(null)}
        />
      )}
      {inlinePhoto && (
        <PhotoFullscreenViewer
          photos={[inlinePhoto]}
          index={0}
          onStep={() => undefined}
          onClose={() => setInlinePhoto(null)}
        />
      )}
    </>
  );
}
